// Discussion thread for a task — the "Summary / activity" panel from the task
// detail mockups. Renders the comment list (newest at the bottom, like chat)
// with author + relative time, plus a composer that posts and refreshes.

import { useEffect, useRef, useState } from "react";
import { addTaskComment, listAssignees, listTaskComments, toggleTaskCommentReaction } from "../lib/api";
import { useApi, useMutation } from "../lib/useApi";
import { formatDate } from "../lib/format";
import { detectMentions } from "../lib/mentions";
import type { TaskCommentRow } from "../lib/types";
import { looksLikeHtml, RichBody, RichTextEditor, richTextIsEmpty } from "./RichText";
import { Alert, Spinner } from "./States";

/** Quick-pick reactions for the comment palette. */
const QUICK_EMOJIS = ["👍", "❤️", "🎉", "👀", "✅", "🙏", "😄", "🚀"];

function initials(name?: string | null): string {
  if (!name) return "•";
  return (
    name
      .trim()
      .split(/\s+/)
      .map((p) => p[0]?.toUpperCase() ?? "")
      .slice(0, 2)
      .join("") || "•"
  );
}

export function TaskComments({
  taskId,
  initial,
  teamsLinked = false,
  readOnly = false,
}: {
  taskId: string;
  initial: TaskCommentRow[];
  /** True when the task's project has a linked Teams channel (0081) — enables
   *  the "Send to Teams" opt-in on the composer. */
  teamsLinked?: boolean;
  /** 0090: read-only shared viewer — read comments + reactions, no posting. */
  readOnly?: boolean;
}) {
  const [comments, setComments] = useState<TaskCommentRow[]>(initial);
  const [body, setBody] = useState("");
  const [pickerFor, setPickerFor] = useState<string | null>(null);
  const [sendTeams, setSendTeams] = useState(false);
  // Roster for @mention resolution (type "@Name" to notify someone).
  const rosterApi = useApi((signal) => listAssignees(signal), []);
  const roster = rosterApi.data?.assignees ?? [];
  const mentioned = detectMentions(body, roster);
  const { run, pending, error } = useMutation((text: string) =>
    addTaskComment(taskId, text, { notify_teams: sendTeams, mentions: detectMentions(text, roster).map((m) => m.id) }),
  );
  const reactMut = useMutation((args: { commentId: string; emoji: string }) =>
    toggleTaskCommentReaction(taskId, args.commentId, args.emoji),
  );
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setComments(initial);
  }, [initial, taskId]);

  async function react(commentId: string, emoji: string) {
    if (readOnly) return;
    setPickerFor(null);
    const res = await reactMut.run({ commentId, emoji });
    if (res) setComments(res.comments);
  }

  async function reload() {
    try {
      const res = await listTaskComments(taskId);
      setComments(res.comments);
    } catch {
      /* keep current list on transient failure */
    }
  }

  const empty = body === "" || richTextIsEmpty(body);

  async function post() {
    if (empty) return;
    const res = await run(body);
    if (res) {
      setBody("");
      await reload();
      endRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }

  return (
    <div className="task-activity">
      <h3 className="drawer-section-title">
        Conversation{comments.length ? ` (${comments.length})` : ""}
      </h3>
      {comments.length === 0 ? (
        <p className="hint" style={{ margin: "0 0 10px" }}>
          No messages yet. Start the conversation — post an update or question for the task's team.
        </p>
      ) : (
        <ul className="comment-list">
          {comments.map((c) => (
            <li key={c.id} className="comment">
              <span className="avatar xs" title={c.author_name ?? ""}>{initials(c.author_name)}</span>
              <div className="comment-body">
                <div className="comment-meta">
                  <strong>{c.author_name ?? "System"}</strong>
                  <span className="faint">{formatDate(c.created_at)}</span>
                </div>
                {/* WYSIWYG comments are stored HTML; legacy ones are plain text. */}
                <div className="comment-text">
                  {looksLikeHtml(c.body) ? <RichBody html={c.body} /> : c.body}
                </div>
                <div className="reaction-row">
                  {(c.reactions ?? []).filter((r) => !readOnly || r.count > 0).map((r) => (
                    <button
                      key={r.emoji}
                      type="button"
                      className={`reaction-chip${r.mine ? " mine" : ""}`}
                      disabled={readOnly || reactMut.pending}
                      onClick={() => react(c.id, r.emoji)}
                      title={r.mine ? "Remove your reaction" : "React"}
                    >
                      <span className="reaction-emoji">{r.emoji}</span> {r.count}
                    </button>
                  ))}
                  {readOnly ? null : (
                  <div className="reaction-add">
                    <button
                      type="button"
                      className="reaction-add-btn"
                      aria-label="Add reaction"
                      disabled={reactMut.pending}
                      onClick={() => setPickerFor((p) => (p === c.id ? null : c.id))}
                    >
                      ☺﹢
                    </button>
                    {pickerFor === c.id ? (
                      <div className="reaction-palette" role="menu">
                        {QUICK_EMOJIS.map((e) => (
                          <button key={e} type="button" onClick={() => react(c.id, e)} title={`React ${e}`}>
                            {e}
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                  )}
                </div>
              </div>
            </li>
          ))}
          <div ref={endRef} />
        </ul>
      )}

      {readOnly ? (
        <p className="hint" style={{ margin: "8px 0 0" }}>You have read-only access — commenting is disabled.</p>
      ) : (
      <>
      {error ? <Alert kind="error">{error}</Alert> : null}
      <div className="comment-composer">
        <div
          style={{ flex: 1, minWidth: 0 }}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === "Enter") post();
          }}
        >
          <RichTextEditor
            value={body}
            onChange={setBody}
            disabled={pending}
            mentionRoster={roster}
            placeholder="Write a comment…  Type @ to notify someone. (⌘/Ctrl+Enter to send)"
          />
          {mentioned.length > 0 ? (
            <div className="hint" style={{ marginTop: 4 }}>
              Will notify: {mentioned.map((m) => m.name).join(", ")}
            </div>
          ) : null}
        </div>
        <button className="btn primary" onClick={post} disabled={pending || empty}>
          {pending ? <Spinner label="Posting…" /> : "Comment"}
        </button>
      </div>
      {teamsLinked ? (
        <label className="toggle" style={{ marginTop: 6 }} title="Also post this comment to the project's linked Microsoft Teams channel">
          <input
            type="checkbox"
            checked={sendTeams}
            disabled={pending}
            onChange={(e) => setSendTeams(e.target.checked)}
          />
          Also send to Teams
        </label>
      ) : null}
      </>
      )}
    </div>
  );
}
